import { inject, Injectable } from '@angular/core';
import {
  ApiService,
  CapturedPhoto,
  IResponseHttp,
  PresignedUrlsPayload,
  ROUTES,
  unwrapResponse,
} from '@core/index';

@Injectable({
  providedIn: 'root',
})
export class UploadsService {
  private readonly api = inject(ApiService);

  public async uploadReceptionEvidences(
    presigned: {
      fileName: string;
      uploadUrl: string;
    }[],
    photos: CapturedPhoto[],
  ): Promise<string[]> {
    const uploadedUrls: string[] = [];

    for (const [index, photo] of photos.entries()) {
      if (!photo.file) {
        throw new Error(`Falta el archivo para ${photo.fileName}.`);
      }

      const target =
        presigned.find((item) => item.fileName === photo.fileName) ?? presigned[index];
      if (!target?.uploadUrl) {
        throw new Error(`Faltan datos de subida para ${photo.fileName}.`);
      }

      await this.uploadFileToUrl(target.uploadUrl, photo.file);
      uploadedUrls.push(target.fileName);
    }
    return uploadedUrls;
  }

  public async uploadFile(uploadUrl: string, file: File): Promise<void> {
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
    });
  }

  public async getPresignedUrls(payload: PresignedUrlsPayload): Promise<
    Array<{
      fileName: string;
      uploadUrl: string;
    }>
  > {
    const response = await this.api.post<IResponseHttp>(
      ROUTES.OPS_UPLOADS_PRESIGNED_URLS,
      payload,
    );
    const data = unwrapResponse<unknown>(response.payload);

    const items = this.extractPresignedItems(data);
    return items.map((item) => {
      const uploadUrl = this.readString(item, ['uploadUrl']);
      const fileName = this.readString(item, ['objectName']) ?? '';

      if (!uploadUrl || !fileName) {
        throw new Error('Respuesta inválida al generar URLs firmadas.');
      }

      return { fileName, uploadUrl };
    });
  }

  private async uploadFileToUrl(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'image/jpeg',
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error('No fue posible subir una de las evidencias.');
    }
  }

  private extractPresignedItems(data: unknown): Record<string, unknown>[] {
    if (Array.isArray(data)) {
      return data.filter((item) => !!item && typeof item === 'object') as Record<
        string,
        unknown
      >[];
    }

    if (data && typeof data === 'object') {
      const objectData = data as Record<string, unknown>;
      const nested = objectData['files'] ?? objectData['items'] ?? objectData['urls'];
      if (Array.isArray(nested)) {
        return nested.filter((item) => !!item && typeof item === 'object') as Record<
          string,
          unknown
        >[];
      }
    }

    return [];
  }

  private readString(
    source: Record<string, unknown>,
    keys: string[],
  ): string | undefined {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return undefined;
  }
}

